import React from 'react';
import { useUserRoles, hasEditAccess, hasCheckinAccess, isUsherOnly, hasManagementAccess, canViewEventManagement, hasFullAccess } from '../utils/roleUtils';
import { useOrganization } from '../contexts/organization-context';

interface RoleBasedAccessProps {
    children: React.ReactNode;
    eventOrganizerId?: string;
    eventUserId?: string;
    requiredAccess: 'edit' | 'checkin' | 'management' | 'full';
    fallback?: React.ReactNode;
}

/**
 * Component that conditionally renders children based on user's role access
 */
export function RoleBasedAccess({
    children,
    eventOrganizerId,
    eventUserId: _eventUserId, // Keep for backward compatibility but no longer used
    requiredAccess,
    fallback = null
}: RoleBasedAccessProps) {
    const { data: userRolesData, isLoading } = useUserRoles();

    if (isLoading) {
        return <div>Loading...</div>;
    }

    const userRoles = userRolesData?.roles || [];
    const ownedOrganizerIds = userRolesData?.ownedOrganizerIds || [];

    let hasAccess = false;

    if (requiredAccess === 'edit') {
        hasAccess = hasEditAccess(userRoles, eventOrganizerId, ownedOrganizerIds);
    } else if (requiredAccess === 'checkin') {
        hasAccess = hasCheckinAccess(userRoles, eventOrganizerId, ownedOrganizerIds);
    } else if (requiredAccess === 'management') {
        hasAccess = hasManagementAccess(userRoles, eventOrganizerId, ownedOrganizerIds);
    } else if (requiredAccess === 'full') {
        hasAccess = hasFullAccess(userRoles, eventOrganizerId, ownedOrganizerIds);
    }

    return hasAccess ? <>{children}</> : <>{fallback}</>;
}

/**
 * Hook to get access information for an event
 */
export function useEventAccess(eventOrganizerId?: string, _eventUserId?: string) { // eventUserId kept for backward compatibility but no longer used
    const { data: userRolesData, isLoading } = useUserRoles();
    const { selectedOrganizerId } = useOrganization();

    const userRoles = userRolesData?.roles || [];
    const ownedOrganizerIds = userRolesData?.ownedOrganizerIds || [];

    // Use selected organization ID or event's organizer ID to check role
    const organizerIdToCheck = selectedOrganizerId || eventOrganizerId;

    // Always return default values to prevent hook order issues
    const canEdit = eventOrganizerId ? hasEditAccess(userRoles, eventOrganizerId, ownedOrganizerIds) : false;
    const canCheckin = eventOrganizerId ? hasCheckinAccess(userRoles, eventOrganizerId, ownedOrganizerIds) : false;
    const canManage = eventOrganizerId ? hasManagementAccess(userRoles, eventOrganizerId, ownedOrganizerIds) : false;
    const canViewManagement = eventOrganizerId ? canViewEventManagement(userRoles, eventOrganizerId, ownedOrganizerIds) : false;
    const canFullAccess = eventOrganizerId ? hasFullAccess(userRoles, eventOrganizerId, ownedOrganizerIds) : false;
    
    // Check if user is usher for the selected organization (or event's organizer)
    const isUsher = organizerIdToCheck
        ? (() => {
            // Normalize IDs for comparison
            const orgId = organizerIdToCheck.toString();
            
            // If user owns the organization, they are not a usher
            const isOwner = ownedOrganizerIds.some(id => {
                const normalizedId = id?.toString() || id;
                return normalizedId === orgId;
            });
            
            if (isOwner) {
                return false;
            }
            
            // Check if user's role for this organization is 'usher'
            const matchingRole = userRoles.find(r => {
                const roleOrgId = r.organizerId?.toString() || r.organizerId;
                return roleOrgId === orgId;
            });
            
            return matchingRole?.role === 'usher';
        })()
        : false;

    return {
        canEdit,
        canCheckin,
        canManage,
        canViewManagement,
        canFullAccess,
        isUsher,
        isLoading,
        userRoles
    };
}

/**
 * Example usage component showing how to restrict access
 */
export function EventManagementExample({ event }: { event: any }) {
    const { canEdit, canCheckin, canManage, canViewManagement, isUsher } = useEventAccess(event.organizerId, event.userId);

    return (
        <div>
            <h3>Event Management</h3>

            {/* Management interface - only for admin/eventmanager/owner */}
            <RoleBasedAccess
                eventOrganizerId={event.organizerId}
                eventUserId={event.userId}
                requiredAccess="management"
                fallback={
                    isUsher ? (
                        <div className="bg-blue-100 p-4 rounded">
                            <p>You have usher access - you can only check-in attendees.</p>
                        </div>
                    ) : (
                        <div>You don't have permission to manage this event</div>
                    )
                }
            >
                <div>
                    <h4>Event Management Controls</h4>

                    {/* Edit controls - only for admin/eventmanager/owner */}
                    <RoleBasedAccess
                        eventOrganizerId={event.organizerId}
                        eventUserId={event.userId}
                        requiredAccess="edit"
                        fallback={<div>You don't have permission to edit this event</div>}
                    >
                        <div>
                            <button>Edit Event</button>
                            <button>Delete Event</button>
                            <button>Manage Tickets</button>
                        </div>
                    </RoleBasedAccess>

                    {/* Check-in controls - for all management roles */}
                    <RoleBasedAccess
                        eventOrganizerId={event.organizerId}
                        eventUserId={event.userId}
                        requiredAccess="checkin"
                        fallback={<div>You don't have permission to check-in attendees</div>}
                    >
                        <div>
                            <button>Check-in Attendees</button>
                            <button>View Attendee List</button>
                        </div>
                    </RoleBasedAccess>
                </div>
            </RoleBasedAccess>

            {/* Usher-only check-in interface */}
            {isUsher && (
                <div className="bg-blue-100 p-4 rounded">
                    <h4>Check-in Interface (Usher Access)</h4>
                    <div>
                        <button>Check-in Attendees</button>
                        <button>View Attendee List</button>
                    </div>
                    <p className="text-sm mt-2">You have usher access - you can check-in attendees but cannot edit events.</p>
                </div>
            )}
        </div>
    );
}
